/**
 * TA Live station health (Diurnal parity C2: 02-003 the six-unit status bar, 02-005 sensor diagnostics;
 * docs/specs/cloud/station-health.md; Diurnal inventory 02 §1.6, §1.7, AC-1.1, AC-1.3, AC-1.5, AC-1.6).
 *
 * Teslyn Cloud checks each organisation's TA Live stations itself (Edge Function `station-poller`, pg_cron): new rows
 * of each station are read by row id (ta-live-config.md §6: "poll by id, not by time"), kept 48 hours in
 * `sensor_reading`, and each station's health is stored (`station_health`): online, offline or no recent readings,
 * the newest reading, the sensor clock's zone and the diagnostics of the station's current sensor day. Teslyn Field
 * and Teslyn Cloud show it read only. The rules are here:
 *
 * - Online while the newest reading reached TA Live within 15 minutes (decision 59: one rule everywhere).
 * - Checked every 15 minutes by day and every 60 minutes at night, 19:00 to 05:00 on the station's clock (the tool's
 *   `StationScheduler`, enhanced-station-monitoring.js:626-655).
 * - Connector health per organisation, not per browser (the tool's circuit breaker, :440-617): 6 checks in a row
 *   that TA Live did not answer pause the checks for 2 hours; "no rows" is never a failure; a manager's reset resumes
 *   at once (AC-1.3).
 * - Sensor diagnostics with the tool's limits (sensor-diagnostics.js:23-26, :78-356): clock drift WARNING over 3 s,
 *   CRITICAL over 300 s, "far out" over 3600 s; a time-zone mismatch when the mean drift is over 30 minutes; latency
 *   jitter MODERATE over 60 s, HIGH over 300 s, after the tool's 1.5 × IQR filter. Two of the tool's bugs are not
 *   copied: latency is worked out over the whole day, not the first 100 readings (02-6), and a day of last year is
 *   dated in last year (02-7). The tool's suggested zone was right only for a sensor set to UTC
 *   (`round(-drift / 3600)`); here it is the station's zone minus the drift.
 * - Offline notices (`diurnal_offline`, `diurnal_online`, alert-notices.ts) for the stations a person has turned
 *   "Offline alerts" on for: once per quiet spell, never for a spell that began more than 2 hours before it was
 *   noticed (decision 445), not again within 2 hours (AC-1.6), and a "back online" notice after one was sent.
 *
 * The Edge Functions use this module through tools/dev/copy-edge-shared.mjs (supabase/functions/_shared/domain/), so
 * every exported type is declared here and nothing imports a type from another module.
 */
import { TALIVE_PAGE_ROWS } from './provider-protocols.js';
import { ALERT_QUEUE_MAX_AGE_MS } from './tracker.js';
import { DEFAULT_PROJECT_OFFSET_H } from './time-model.js';

// ------------------------------------------------------------------------------------------------ limits

/** Online while the newest reading is at most this old (decision 59, spec diurnal-qc.md §5). */
export const STATION_ONLINE_MINUTES = 15;
/** How often the cloud checks the stations: by day, and at night on the station's clock (19:00 to 05:00). */
export const STATION_POLL_DAY_MS = 15 * 60_000;
export const STATION_POLL_NIGHT_MS = 60 * 60_000;
export const STATION_NIGHT_FROM_H = 19;
export const STATION_NIGHT_TO_H = 5;
/** The tool's default sensor clock zone (Perth), used when no station has one. */
export const STATION_DEFAULT_CLOCK_H = 8;
/** Checks in a row TA Live did not answer before the checks pause, and for how long (the tool's breaker). */
export const STATION_BREAKER_FAILURES = 6;
export const STATION_BREAKER_PAUSE_MS = 2 * 3_600_000;
/** A run holds an organisation this long, so an overlapping run never checks it twice. */
export const STATION_CLAIM_SECONDS = 240;
/** "The newest row of one station" is only quick within the newest 500,000 TA Live rows (ta-live-config.md §5). */
export const STATION_ID_FLOOR = 500_000;
/** A station read for the first time (or after a long gap): the newest 100,000 rows, about a day at today's rate. */
export const STATION_FIRST_IDS = 100_000;
/** Rows per page, and pages per station per run (a run catches up the rest next time). */
export const STATION_PAGE_ROWS = TALIVE_PAGE_ROWS;
export const STATION_MAX_PAGES = 8;
/** Readings are kept this long in Teslyn Cloud (TA Live keeps everything). */
export const STATION_READINGS_KEEP_H = 48;
/** A station's offline notice is not sent again within this time (AC-1.6). */
export const STATION_OFFLINE_REPEAT_MS = 2 * 3_600_000;
/** A check older than this is shown amber, "not checked lately". */
export const STATION_STALE_MS = STATION_POLL_NIGHT_MS + 15 * 60_000;

/** Clock drift limits (sensor-diagnostics.js:23-24, :226): seconds. */
export const CLOCK_DRIFT_WARNING_S = 3;
export const CLOCK_DRIFT_CRITICAL_S = 300;
export const CLOCK_DRIFT_FAR_OUT_S = 3600;
/** A mean drift over half an hour is a clock zone, not a drift (sensor-diagnostics.js:255). */
export const TIME_ZONE_MISMATCH_S = 1800;
/** Decision 777: a clock drift over 2 minutes (after the usual delay) raises an alert to the field crew. */
export const CLOCK_DRIFT_ALERT_S = 120;
/** A day needs this many readings before its clock alert is raised (a few readings give a noisy mean). */
export const STATION_CLOCK_ALERT_MIN_READINGS = 100;
/** Decision 774: a sensor day needs this many readings to work out its clock zone (else the unit's last known). */
export const STATION_MIN_DETECT_READINGS = 10;
/**
 * Decision 776: the unit's usual network delay (the day's median delay) is taken off before the drift is judged; a
 * median beyond this is not network delay but the clock, so at most this much is taken off.
 */
export const STATION_USUAL_DELAY_MAX_S = 10;
/** Latency jitter limits (sensor-diagnostics.js:25-26): seconds. */
export const LATENCY_MODERATE_S = 60;
export const LATENCY_HIGH_S = 300;

// ------------------------------------------------------------------------------------------------ types

export type StationHealthStatus = 'online' | 'offline' | 'no_recent';
export type ConnectorStateId =
  'waiting' | 'ok' | 'failing' | 'paused' | 'refused' | 'not_set_up' | 'rate_limited';
export type DriftLevel = 'ok' | 'warning' | 'critical';
export type DriftTrend = 'stable' | 'increasing' | 'decreasing';
export type LatencyLevel = 'normal' | 'moderate' | 'high';

/** The reading time and insert time of a TA Live row (`TaLiveRow` has both). */
export interface TaLiveTimedRow {
  readonly julianDate: number;
  readonly tHour: number;
  readonly tMinute: number;
  readonly tSecond: number;
  /** ISO UTC: when TA Live stored the row. */
  readonly created_at: string;
}

/** A row of the proxy's `talive` `since` answer. */
export interface TaLiveSinceRow extends TaLiveTimedRow {
  readonly id: number;
  readonly magValue: number;
  readonly Good: boolean;
  readonly IMEIString?: string;
}

/** One stored reading (`sensor_reading`). */
export interface StationReadingRow {
  readonly source_id: number;
  /** The sensor's clock, no zone: `2026-10-18T00:00:00`. */
  readonly sensor_time: string;
  /** The reading's UTC time with the sensor clock zone found for the batch. */
  readonly at_utc: string;
  /** When TA Live stored it (UTC, as TA Live gave it). */
  readonly received_at: string;
  readonly nt: number;
  readonly good: boolean;
}

export interface ClockDrift {
  readonly mean_s: number;
  readonly min_s: number;
  readonly max_s: number;
  readonly std_s: number;
  readonly level: DriftLevel;
  readonly trend: DriftTrend;
  /** Decision 777: over 2 minutes, an alert to the field crew. */
  readonly alert: boolean;
}

/** Where the clock zone the readings are read with came from (decision 774). */
export type ClockZoneSource = 'detected' | 'registered' | 'default';

export interface TimeZoneCheck {
  /** False without a zone found for this day and for the day before (too few readings). */
  readonly checked: boolean;
  /** The zone found for this day differs from the day before's: a unit moved, or its clock set to another zone. */
  readonly mismatch: boolean;
  /** The zone the sensor now looks set to, when it changed. */
  readonly suggested_offset_h: number | null;
  /** The zone found for the day before, when there were readings. */
  readonly previous_offset_h?: number | null;
  /**
   * What the zone was judged against (decision 788): the project the unit is linked to that day, or, with no project
   * linked, the zone found the day before.
   */
  readonly against?: 'project' | 'day_before';
  /** The project's zone and number when judged against a project. */
  readonly project_offset_h?: number | null;
  readonly project_number?: string | null;
}

export interface LatencyJitter {
  readonly mean_s: number;
  readonly min_s: number;
  readonly max_s: number;
  readonly jitter_s: number;
  /** Readings left after the 1.5 × IQR filter. */
  readonly kept: number;
  readonly level: LatencyLevel;
}

/** Sensor diagnostics of one station's sensor day (02-005), as stored in `station_health.diagnostics`. */
export interface StationDiagnostics {
  readonly samples: number;
  /** The clock zone the readings are read with: the zone found (decision 774), else the unit's registered one. */
  readonly clock_offset_h: number;
  /** The zone found from the day's readings (median, to 0.5 h); null with too few readings. */
  readonly detected_offset_h: number | null;
  readonly offset_source?: ClockZoneSource;
  /** Decision 776: the usual network delay taken off before the drift is judged (the day's median, 0 to 10 s). */
  readonly usual_delay_s?: number;
  readonly drift: ClockDrift;
  readonly time_zone: TimeZoneCheck;
  readonly latency: LatencyJitter;
}

/** A station as the poller gets it (`station_poll_candidates`), with its stored health. */
export interface StationPollStation {
  readonly id: string;
  readonly imei: string;
  readonly name: string;
  readonly station_number: string;
  readonly clock_offset_h: number | null;
  readonly offline_alerts: boolean;
  readonly last_id: number | null;
  readonly status: StationHealthStatus | null;
  readonly last_reading_at: string | null;
  readonly last_sensor_time: string | null;
  readonly last_value_nt: number | null;
  readonly last_good: boolean | null;
  readonly detected_offset_h: number | null;
  readonly offline_since: string | null;
  readonly offline_alert_at: string | null;
  readonly diagnostics_day: string | null;
  /** The live projects this station is linked to today (parity C3, `project_base_station`): its notices go to their
   *  crews (decision 53); none, to the organisation's field crew. */
  readonly project_ids?: readonly string[];
  /** The same projects with their zones (their Diurnal QC UTC offset, null: not set), the most recent link first
   *  (decision 788: the unit's zone is judged against the project's). */
  readonly projects?: readonly StationLinkedProject[];
}

/** A project a unit is linked to (decision 788). */
export interface StationLinkedProject {
  readonly id: string;
  readonly number: string;
  /** The project's UTC offset from its Diurnal QC values; null when the project sets none (the company default). */
  readonly offset_h: number | null;
  readonly starts_on: string;
}

/**
 * The zone a unit's clock should be on (decision 788): the zone of the project it works on (the most recent link),
 * its own UTC offset or the company default (UTC+8, decisions 19 and 20); null when the unit is on no project.
 */
export function stationProjectZone(
  projects: readonly StationLinkedProject[] | null | undefined,
): { readonly offsetH: number; readonly number: string } | null {
  const p = projects?.[0];
  if (!p) return null;
  return { offsetH: p.offset_h ?? DEFAULT_PROJECT_OFFSET_H, number: p.number };
}

/** An organisation's TA Live connection with its poll state and active stations. */
export interface StationPollCandidate {
  readonly integration_id: string;
  readonly tenant_id: string;
  readonly tenant_status: string;
  readonly integration_status: string;
  readonly integration_updated_at: string | null;
  readonly state: ConnectorStateId | null;
  readonly failures: number;
  readonly paused_until: string | null;
  readonly last_poll_at: string | null;
  readonly next_poll_at: string | null;
  /** The organisation's online threshold, minutes (decision 59: a tenant setting, 15 by default). */
  readonly online_minutes?: number | null;
  readonly stations: readonly StationPollStation[];
}

/** What `station_poll_store` keeps for one station. `diagnostics` left out keeps the stored ones. */
export interface StationHealthUpdate {
  readonly station_id: string;
  readonly status: StationHealthStatus;
  readonly last_reading_at: string | null;
  readonly last_sensor_time: string | null;
  readonly last_value_nt: number | null;
  readonly last_good: boolean | null;
  readonly detected_offset_h: number | null;
  readonly last_id: number | null;
  readonly message: string;
  readonly diagnostics?: StationDiagnostics | null;
  readonly diagnostics_day?: string | null;
  readonly offline_since: string | null;
  readonly offline_alert_at: string | null;
}

/** What `station_poll_store` keeps for the organisation's TA Live connection. */
export interface ConnectorUpdate {
  readonly state: ConnectorStateId;
  readonly message: string;
  readonly failures: number;
  readonly paused_until: string | null;
  readonly last_poll_at: string;
  readonly last_ok_at: string | null;
  readonly next_poll_at: string;
  readonly checked: number;
  readonly stored: number;
}

/** One run's result for one organisation. */
export type StationRunResult =
  | { readonly kind: 'ok'; readonly checked: number; readonly unanswered: number; readonly stored: number }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'refused'; readonly message: string }
  | { readonly kind: 'not_set_up'; readonly message: string }
  | { readonly kind: 'rate_limited'; readonly retryAfterS: number }
  /** Teslyn's own failure inside the run (a database refusal, an error in the code): not counted against TA Live. */
  | { readonly kind: 'failed'; readonly message: string };

/** An event to raise (`alert_raise`), the same shape as alert-notices.ts `NoticeEvent`. */
export interface StationNoticeEvent {
  readonly event_key: string;
  readonly kind: 'diurnal_offline' | 'diurnal_online' | 'station_clock' | 'station_time_zone';
  readonly title: string;
  readonly body: string;
  readonly occurred_at: string;
  readonly project_id: string | null;
  readonly asset_id: string | null;
  readonly follows_key: string | null;
  readonly detail: Readonly<Record<string, string | number | boolean | null>>;
}

// ------------------------------------------------------------------------------------------------ times

const MS_H = 3_600_000;
const iso = (ms: number): string => new Date(ms).toISOString();
const r3 = (x: number): number => Math.round(x * 1000) / 1000;
const half = (h: number): number => Math.round(h * 2) / 2;

/** "UTC+8", "UTC+9.5", "UTC−3", "UTC". */
export function stationClockLabel(offsetH: number | null | undefined): string {
  if (offsetH === null || offsetH === undefined || !Number.isFinite(offsetH)) return 'UTC?';
  if (offsetH === 0) return 'UTC';
  return `UTC${offsetH > 0 ? '+' : '−'}${Math.abs(offsetH)}`;
}

/**
 * A row's sensor clock read as if it were UTC (ms), and its insert time (ms); null when the time is impossible. The
 * year comes from the insert date, and a day number far from it takes the neighbouring year, so last year's days and
 * New Year are dated right (bug 02-7 not copied; the same rule as ta-live.ts `taLiveReadings`).
 */
export function taLiveRowTimes(
  r: TaLiveTimedRow,
): { readonly sensorMs: number; readonly createdMs: number } | null {
  if (r.tHour < 0 || r.tHour > 23 || r.tMinute < 0 || r.tMinute > 59 || r.tSecond < 0 || r.tSecond > 59)
    return null;
  if (r.julianDate < 1 || r.julianDate > 366) return null;
  const createdMs = Date.parse(r.created_at);
  if (!Number.isFinite(createdMs)) return null;
  let year = new Date(createdMs).getUTCFullYear();
  const createdDay = Math.floor((createdMs - Date.UTC(year, 0, 1)) / 86_400_000) + 1;
  if (r.julianDate - createdDay > 180) year--;
  else if (createdDay - r.julianDate > 180) year++;
  const sensorMs =
    Date.UTC(year, 0, 1) +
    (r.julianDate - 1) * 86_400_000 +
    (r.tHour * 3600 + r.tMinute * 60 + r.tSecond) * 1000;
  return { sensorMs, createdMs };
}

/** Each row's delay: its insert time minus its sensor clock read as UTC, in seconds (a UTC+8 clock gives about −28,800). */
export function taLiveDelays(rows: readonly TaLiveTimedRow[]): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const t = taLiveRowTimes(r);
    if (t) out.push(r3((t.createdMs - t.sensorMs) / 1000));
  }
  return out;
}

function median(sorted: readonly number[]): number {
  const m = sorted.length >> 1;
  return sorted.length % 2 ? sorted[m]! : (sorted[m - 1]! + sorted[m]!) / 2;
}

/** The sensor clock's zone from the delays: the median, to 0.5 h (as `detectSensorOffsetH`); null with none. */
export function offsetFromDelays(delays: readonly number[]): number | null {
  if (delays.length === 0) return null;
  const s = delays.map((d) => -d / 3600).sort((a, b) => a - b);
  const h = half(median(s));
  return h === 0 ? 0 : h;
}

/** `2026-10-18T00:00:00` for a sensor clock read as UTC. */
const naive = (ms: number): string => iso(ms).slice(0, 19);

/**
 * The poller's rows to stored readings: the sensor clock as it reads, the UTC time with the zone found for the batch
 * (or `fallbackOffsetH` when the batch has none), TA Live's insert time and the value in nT. Rows with an impossible
 * time are left out and counted.
 */
export function stationReadingRows(
  rows: readonly TaLiveSinceRow[],
  fallbackOffsetH: number,
): { readonly rows: StationReadingRow[]; readonly offsetH: number; readonly rejected: number } {
  const offsetH = offsetFromDelays(taLiveDelays(rows)) ?? fallbackOffsetH;
  const out: StationReadingRow[] = [];
  let rejected = 0;
  for (const r of rows) {
    const t = taLiveRowTimes(r);
    if (!t || !Number.isFinite(r.magValue)) {
      rejected++;
      continue;
    }
    out.push({
      source_id: r.id,
      sensor_time: naive(t.sensorMs),
      at_utc: iso(t.sensorMs - Math.round(offsetH * MS_H)),
      received_at: r.created_at,
      nt: r.magValue / 10,
      good: r.Good,
    });
  }
  return { rows: out, offsetH, rejected };
}

// ------------------------------------------------------------------------------------------------ diagnostics

export function driftLevel(meanS: number): DriftLevel {
  const a = Math.abs(meanS);
  return a > CLOCK_DRIFT_CRITICAL_S ? 'critical' : a > CLOCK_DRIFT_WARNING_S ? 'warning' : 'ok';
}

export function latencyLevel(jitterS: number): LatencyLevel {
  return jitterS > LATENCY_HIGH_S ? 'high' : jitterS > LATENCY_MODERATE_S ? 'moderate' : 'normal';
}

function stats(xs: readonly number[]): { mean: number; min: number; max: number; std: number } {
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (const x of xs) {
    sum += x;
    if (x < min) min = x;
    if (x > max) max = x;
  }
  const mean = sum / xs.length;
  let v = 0;
  for (const x of xs) v += (x - mean) * (x - mean);
  return { mean, min, max, std: Math.sqrt(v / xs.length) };
}

const meanOf = (xs: readonly number[], from: number, to: number): number => {
  let s = 0;
  for (let i = from; i < to; i++) s += xs[i]!;
  return s / (to - from);
};

export interface SensorDiagnosticsOptions {
  /** The clock zone found for the day before (decision 774: a change is a time-zone mismatch). */
  readonly previousOffsetH?: number | null;
  /**
   * The zone of the project the unit works on (decision 788): when given, a zone other than the project's is the
   * mismatch, and the day before is not used.
   */
  readonly project?: { readonly offsetH: number; readonly number: string } | null;
}

/**
 * The sensor diagnostics of a day's readings (02-005), from each reading's delay in reading order (`taLiveDelays`,
 * or the database's `station_day_delays`).
 *
 * - Decision 774: the clock zone is worked out from the readings themselves, as the Diurnal tool did (each reading's
 *   own time against TA Live's UTC time, `calculateUTCOffsetFromCreatedAt`, data.js:206-306: the median, to the half
 *   hour), here over every reading of the day; `clockOffsetH` (the unit's registered zone) is used only with fewer
 *   than 10 readings. A zone found that differs from the day before's is a time-zone mismatch.
 * - Decision 776: the drift is the delay less the unit's usual network delay (the day's median delay, at most 10 s),
 *   so "Warning" means the clock really is off; over 2 minutes is an alert (decision 777).
 * - Latency jitter: the tool's 1.5 x IQR filter over every reading of the day (bug 02-6 not copied).
 * Null with no readings.
 */
export function sensorDiagnostics(
  delays: readonly number[],
  clockOffsetH: number | null,
  opts: SensorDiagnosticsOptions = {},
): StationDiagnostics | null {
  if (delays.length === 0) return null;
  const detected = delays.length >= STATION_MIN_DETECT_READINGS ? offsetFromDelays(delays) : null;
  const source: ClockZoneSource =
    detected !== null ? 'detected' : clockOffsetH !== null ? 'registered' : 'default';
  const offset = detected ?? clockOffsetH ?? STATION_DEFAULT_CLOCK_H;
  // each reading's delay with that zone: the network delay plus the clock's error
  const raw = delays.map((d) => d + offset * 3600);
  const sortedRaw = [...raw].sort((x, y) => x - y);
  const usual = Math.min(STATION_USUAL_DELAY_MAX_S, Math.max(0, median(sortedRaw)));
  const drifts = raw.map((d) => d - usual);
  const s = stats(drifts);
  // trend: the last quarter's mean against the first quarter's, over 10 readings (sensor-diagnostics.js:174-186)
  let trend: DriftTrend = 'stable';
  if (drifts.length > 10) {
    const q = Math.floor(drifts.length / 4);
    const diff = meanOf(drifts, drifts.length - q, drifts.length) - meanOf(drifts, 0, q);
    if (Math.abs(diff) > 1) trend = diff > 0 ? 'increasing' : 'decreasing';
  }
  const previous = opts.previousOffsetH ?? null;
  const project = opts.project ?? null;
  const checked = detected !== null && (project !== null || previous !== null);
  const mismatch = checked && detected !== (project !== null ? project.offsetH : previous);
  // latency: every reading of the day (bug 02-6: the tool used the first 100), 1.5 x IQR (:299-356)
  const q1 = sortedRaw[Math.floor(sortedRaw.length * 0.25)]!;
  const q3 = sortedRaw[Math.floor(sortedRaw.length * 0.75)]!;
  const iqr = q3 - q1;
  const kept = raw.filter((d) => d >= q1 - 1.5 * iqr && d <= q3 + 1.5 * iqr);
  const l = stats(kept);
  const jitter = l.max - l.min;
  return {
    samples: drifts.length,
    clock_offset_h: offset,
    detected_offset_h: detected,
    offset_source: source,
    usual_delay_s: r3(usual),
    drift: {
      mean_s: r3(s.mean),
      min_s: r3(s.min),
      max_s: r3(s.max),
      std_s: r3(s.std),
      level: driftLevel(s.mean),
      trend,
      alert: Math.abs(s.mean) > CLOCK_DRIFT_ALERT_S,
    },
    time_zone: {
      checked,
      mismatch,
      suggested_offset_h: mismatch ? detected : null,
      previous_offset_h: previous,
      against: project !== null ? 'project' : 'day_before',
      project_offset_h: project?.offsetH ?? null,
      project_number: project?.number ?? null,
    },
    latency: {
      mean_s: r3(l.mean),
      min_s: r3(l.min),
      max_s: r3(l.max),
      jitter_s: r3(jitter),
      kept: kept.length,
      level: latencyLevel(jitter),
    },
  };
}

/** Diagnostics straight from TA Live rows (Teslyn Field's loaded day, tests). */
export function stationDiagnosticsFromRows(
  rows: readonly TaLiveTimedRow[],
  clockOffsetH: number | null,
  opts: SensorDiagnosticsOptions = {},
): StationDiagnostics | null {
  const ordered = [...rows]
    .map((r) => ({ r, t: taLiveRowTimes(r) }))
    .filter((x) => x.t !== null)
    .sort((a, b) => a.t!.sensorMs - b.t!.sensorMs || a.t!.createdMs - b.t!.createdMs);
  return sensorDiagnostics(
    ordered.map((x) => r3((x.t!.createdMs - x.t!.sensorMs) / 1000)),
    clockOffsetH,
    opts,
  );
}

const sec = (s: number): string => {
  const a = Math.abs(s);
  const [v, unit] =
    a >= 3600
      ? [(a / 3600).toFixed(1), 'h']
      : a >= 120
        ? [(a / 60).toFixed(1), 'min']
        : [a.toFixed(a >= 10 ? 0 : 1), 's'];
  // no sign on a value that shows as nought
  return `${s < 0 && Number(v) !== 0 ? '−' : ''}${v} ${unit}`;
};

/** "UTC+8 (detected)", "UTC+8 (registered)" (decision 774). */
export function clockZoneLabel(
  offsetH: number | null | undefined,
  source: ClockZoneSource | undefined,
): string {
  const l = stationClockLabel(offsetH);
  return source === 'detected' ? `${l} (detected)` : source === 'registered' ? `${l} (registered)` : l;
}

/** What to do about the clock, in the help voice (the tool's recommendations, sensor-diagnostics.js:239-262). */
export function clockAdvice(d: StationDiagnostics): string {
  if (d.time_zone.mismatch && d.time_zone.against === 'project')
    return `The sensor clock looks set to ${stationClockLabel(d.time_zone.suggested_offset_h)}, but project ${d.time_zone.project_number ?? ''} is on ${stationClockLabel(d.time_zone.project_offset_h)}. Set the unit's clock to the project's zone, or check the project the unit is linked to.`;
  if (d.time_zone.mismatch)
    return `The sensor clock now looks set to ${stationClockLabel(d.time_zone.suggested_offset_h)}; the day before it was ${stationClockLabel(d.time_zone.previous_offset_h)}. Check the unit's clock zone: the unit was moved, or its clock was set to another zone.`;
  if (d.drift.alert)
    return `The sensor clock is ${sec(Math.abs(d.drift.mean_s))} out after its usual ${sec(d.usual_delay_s ?? 0)} network delay. Check its GPS lock or set the time.`;
  if (d.drift.level !== 'ok')
    return 'Watch the clock: it is a few seconds out after its usual network delay.';
  return 'The clock is within limits.';
}

const DRIFT_WORD: Readonly<Record<DriftLevel, string>> = {
  ok: 'OK',
  warning: 'Warning',
  critical: 'Critical',
};
const LATENCY_WORD: Readonly<Record<LatencyLevel, string>> = {
  normal: 'Normal',
  moderate: 'Moderate',
  high: 'High',
};
const TREND_WORD: Readonly<Record<DriftTrend, string>> = {
  stable: 'steady',
  increasing: 'growing',
  decreasing: 'shrinking',
};

/** The three lines of the diagnostics: clock drift, time zone, latency. */
export function diagnosticsLines(d: StationDiagnostics): {
  readonly drift: string;
  readonly timeZone: string;
  readonly latency: string;
} {
  return {
    drift: `Clock drift ${DRIFT_WORD[d.drift.level]}: ${sec(d.drift.mean_s)} on average after its usual ${sec(d.usual_delay_s ?? 0)} delay (${sec(d.drift.min_s)} to ${sec(d.drift.max_s)}), ${TREND_WORD[d.drift.trend]}.`,
    timeZone:
      d.detected_offset_h === null
        ? `Time zone not found: too few readings, read as ${clockZoneLabel(d.clock_offset_h, d.offset_source)}.`
        : d.time_zone.against === 'project'
          ? d.time_zone.mismatch
            ? `Time zone not the project's: the sensor looks set to ${clockZoneLabel(d.detected_offset_h, 'detected')}, project ${d.time_zone.project_number ?? ''} is on ${stationClockLabel(d.time_zone.project_offset_h)}.`
            : `Time zone ${clockZoneLabel(d.detected_offset_h, 'detected')}, as project ${d.time_zone.project_number ?? ''}.`
          : d.time_zone.mismatch
            ? `Time zone changed: the sensor now looks set to ${clockZoneLabel(d.detected_offset_h, 'detected')}, ${stationClockLabel(d.time_zone.previous_offset_h)} the day before.`
            : d.time_zone.checked
              ? `Time zone ${clockZoneLabel(d.detected_offset_h, 'detected')}, as the day before.`
              : `Time zone ${clockZoneLabel(d.detected_offset_h, 'detected')}; no readings of the day before to compare.`,
    latency: `Latency jitter ${LATENCY_WORD[d.latency.level]}: ${sec(d.latency.jitter_s)} (delay ${sec(d.latency.min_s)} to ${sec(d.latency.max_s)} over ${d.latency.kept.toLocaleString('en-AU')} of ${d.samples.toLocaleString('en-AU')} readings).`,
  };
}

// ------------------------------------------------------------------------------------------------ polling

/** Online, offline, or no reading above the id floor (TA Live cannot say when a long-quiet station last sent). */
export function stationHealthStatus(
  lastReceivedMs: number | null,
  nowMs: number,
  onlineMinutes: number = STATION_ONLINE_MINUTES,
): StationHealthStatus {
  if (lastReceivedMs === null || !Number.isFinite(lastReceivedMs)) return 'no_recent';
  return nowMs - lastReceivedMs <= onlineMinutes * 60_000 ? 'online' : 'offline';
}

/** Whether `nowMs` is night (19:00 to 05:00) on a clock `offsetH` hours east of UTC. */
export function stationNight(nowMs: number, offsetH: number): boolean {
  const h = new Date(nowMs + Math.round(offsetH * MS_H)).getUTCHours();
  return h >= STATION_NIGHT_FROM_H || h < STATION_NIGHT_TO_H;
}

export function stationPollInterval(nowMs: number, offsetH: number): number {
  return stationNight(nowMs, offsetH) ? STATION_POLL_NIGHT_MS : STATION_POLL_DAY_MS;
}

/** The clock zone the schedule follows: the zone most of the organisation's stations have, else Perth. */
export function scheduleOffsetH(
  stations: readonly (Pick<StationPollStation, 'clock_offset_h'> & {
    readonly detected_offset_h?: number | null;
  })[],
): number {
  const n = new Map<number, number>();
  // decision 774: the zone found from each unit's readings, else its registered one
  for (const st of stations) {
    const h = st.detected_offset_h ?? st.clock_offset_h;
    if (h !== null && h !== undefined) n.set(h, (n.get(h) ?? 0) + 1);
  }
  let best: number | null = null;
  for (const [h, c] of n) if (best === null || c > n.get(best)!) best = h;
  return best ?? STATION_DEFAULT_CLOCK_H;
}

/** Whether an organisation's TA Live is due a check now. */
export function stationPollDue(c: StationPollCandidate, nowMs: number): boolean {
  if (c.tenant_status !== 'active' || c.stations.length === 0) return false;
  if (c.paused_until && Date.parse(c.paused_until) > nowMs) return false;
  // a refused key is asked again as soon as it is replaced
  if (c.state === 'refused' && c.integration_updated_at && c.last_poll_at)
    if (Date.parse(c.integration_updated_at) > Date.parse(c.last_poll_at)) return true;
  if (!c.next_poll_at) return true;
  const next = Date.parse(c.next_poll_at);
  return !Number.isFinite(next) || nowMs >= next - 30_000;
}

/** The id to read a station's new rows above: its last id, unless it is below the floor or never read. */
export function stationSinceFrom(lastId: number | null, maxId: number): number {
  if (lastId !== null && lastId >= maxId - STATION_ID_FLOOR) return Math.max(0, lastId);
  return Math.max(0, maxId - STATION_FIRST_IDS);
}

const hhmm = (ms: number): string => `${iso(ms).slice(11, 16)} UTC`;
const plural = (n: number, one: string, many: string): string =>
  `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;

/** The connection's state after a run (the tool's breaker, per organisation). */
export function connectorAfterRun(
  failures: number,
  r: StationRunResult,
  nowMs: number,
  offsetH: number,
): ConnectorUpdate {
  const base = { last_poll_at: iso(nowMs), last_ok_at: null, paused_until: null, checked: 0, stored: 0 };
  const every = stationPollInterval(nowMs, offsetH);
  switch (r.kind) {
    case 'ok':
      return {
        ...base,
        state: 'ok',
        message:
          `TA Live: ${plural(r.checked, 'unit', 'units')} checked, ${plural(r.stored, 'reading', 'readings')} stored` +
          (r.unanswered > 0 ? `, ${plural(r.unanswered, 'unit', 'units')} not answered` : ''),
        failures: 0,
        last_ok_at: iso(nowMs),
        next_poll_at: iso(nowMs + every),
        checked: r.checked,
        stored: r.stored,
      };
    case 'error': {
      const n = failures + 1;
      if (n >= STATION_BREAKER_FAILURES) {
        const until = nowMs + STATION_BREAKER_PAUSE_MS;
        return {
          ...base,
          state: 'paused',
          message: `TA Live did not answer ${n} checks in a row (${r.message}). Checks are paused until ${hhmm(until)}; a manager can reset the connection.`,
          failures: n,
          paused_until: iso(until),
          next_poll_at: iso(until),
        };
      }
      return {
        ...base,
        state: 'failing',
        message: `TA Live did not answer (${r.message}). ${plural(n, 'check', 'checks')} in a row; checks pause after ${STATION_BREAKER_FAILURES}.`,
        failures: n,
        next_poll_at: iso(nowMs + every),
      };
    }
    case 'refused':
      return {
        ...base,
        state: 'refused',
        message: r.message,
        failures: failures + 1,
        next_poll_at: iso(nowMs + STATION_POLL_NIGHT_MS),
      };
    case 'not_set_up':
      return {
        ...base,
        state: 'not_set_up',
        message: r.message,
        failures,
        next_poll_at: iso(nowMs + STATION_POLL_NIGHT_MS),
      };
    case 'failed':
      return {
        ...base,
        state: 'failing',
        message: `The check could not finish: ${r.message}`.slice(0, 300),
        failures,
        next_poll_at: iso(nowMs + every),
      };
    case 'rate_limited':
      return {
        ...base,
        state: 'rate_limited',
        message: 'Too many TA Live requests this minute. Trying again shortly.',
        failures,
        next_poll_at: iso(nowMs + Math.max(60, r.retryAfterS) * 1000),
      };
  }
}

// ------------------------------------------------------------------------------------------------ notices

const unitName = (s: Pick<StationPollStation, 'name' | 'station_number'>): string =>
  s.station_number ? `${s.name} (${s.station_number})` : s.name;
const noticeUtc = (ms: number): string => `${iso(ms).slice(0, 19).replace('T', ' ')} UTC`;
const minutes = (ms: number): string => plural(Math.max(0, Math.round(ms / 60_000)), 'minute', 'minutes');

export interface StationNoticeInput {
  readonly station: Pick<
    StationPollStation,
    'imei' | 'name' | 'station_number' | 'offline_alerts' | 'offline_since' | 'offline_alert_at'
  >;
  readonly status: StationHealthStatus;
  readonly lastReceivedMs: number | null;
  readonly nowMs: number;
  readonly onlineMinutes?: number;
}

export interface StationNoticeResult {
  readonly events: StationNoticeEvent[];
  /** The newest reading before the station went quiet, kept while it is offline. */
  readonly offline_since: string | null;
}

export const offlineKey = (imei: string, lastReceivedMs: number): string =>
  `diurnal_offline:ta_live:${imei}:${lastReceivedMs}`;

/**
 * The offline and back-online notices of one check of one station (AC-1.6). Offline: the station has "Offline
 * alerts" on, it went quiet at most 2 hours before this check (decision 445), and no offline notice of it went in
 * the last 2 hours; the key holds the newest reading's time, so every check of one quiet spell gives the same key.
 * Back online: it follows the offline notice of the spell, so it is raised only after one was.
 */
export function stationNotices(input: StationNoticeInput): StationNoticeResult {
  const { station, status, lastReceivedMs, nowMs } = input;
  const online = (input.onlineMinutes ?? STATION_ONLINE_MINUTES) * 60_000;
  const name = unitName(station);
  const detail = (last: number | null) => ({
    station: station.name,
    station_number: station.station_number,
    imei: station.imei,
    source: 'ta_live',
    last_reading_at: last === null ? null : iso(last),
  });
  if (status === 'offline' && lastReceivedMs !== null) {
    const since = iso(lastReceivedMs);
    const quietFor = nowMs - lastReceivedMs;
    const lastAlert = station.offline_alert_at ? Date.parse(station.offline_alert_at) : NaN;
    const repeatOk = !Number.isFinite(lastAlert) || nowMs - lastAlert >= STATION_OFFLINE_REPEAT_MS;
    const fresh = quietFor - online <= ALERT_QUEUE_MAX_AGE_MS;
    if (!station.offline_alerts || !fresh || !repeatOk) return { events: [], offline_since: since };
    return {
      offline_since: since,
      events: [
        {
          event_key: offlineKey(station.imei, lastReceivedMs),
          kind: 'diurnal_offline',
          title: `Station offline: ${name}`.slice(0, 120),
          body: `${station.name} has sent nothing to TA Live since ${noticeUtc(lastReceivedMs)} (${minutes(quietFor)}).`,
          occurred_at: iso(lastReceivedMs + online),
          project_id: null,
          asset_id: null,
          follows_key: null,
          detail: detail(lastReceivedMs),
        },
      ],
    };
  }
  if (status === 'online') {
    const was = station.offline_since ? Date.parse(station.offline_since) : NaN;
    if (!Number.isFinite(was) || lastReceivedMs === null || !station.offline_alerts)
      return { events: [], offline_since: null };
    return {
      offline_since: null,
      events: [
        {
          event_key: `diurnal_online:ta_live:${station.imei}:${was}`,
          kind: 'diurnal_online',
          title: `Station back online: ${name}`.slice(0, 120),
          body: `${station.name} is sending again: newest reading ${noticeUtc(lastReceivedMs)}, after ${minutes(lastReceivedMs - was)} quiet.`,
          occurred_at: iso(lastReceivedMs),
          project_id: null,
          asset_id: null,
          follows_key: offlineKey(station.imei, was),
          detail: detail(lastReceivedMs),
        },
      ],
    };
  }
  // no reading above the floor: quiet for days, nothing to say again
  return { events: [], offline_since: station.offline_since };
}

export interface StationClockNoticeInput {
  readonly station: Pick<StationPollStation, 'imei' | 'name' | 'station_number'>;
  readonly diagnostics: StationDiagnostics | null;
  /** The sensor day the diagnostics are of, `YYYY-MM-DD`. */
  readonly day: string;
  readonly nowMs: number;
}

/**
 * Decision 777: a clock drift over 2 minutes (after the usual delay) or a time-zone change raises an alert to the
 * field crew, as a station going offline does. Once per unit and sensor day (the key holds the day; a zone change also
 * the new zone), and only once the day has 100 readings. Raised for every unit that sends (a unit in the store sends
 * nothing, so it raises nothing); not tied to the unit's "Offline alerts" switch.
 */
export function stationClockNotices(input: StationClockNoticeInput): StationNoticeEvent[] {
  const d = input.diagnostics;
  if (!d || d.samples < STATION_CLOCK_ALERT_MIN_READINGS) return [];
  const { station, day } = input;
  const name = unitName(station);
  const detail = {
    station: station.name,
    station_number: station.station_number,
    imei: station.imei,
    source: 'ta_live',
    sensor_day: day,
  };
  const out: StationNoticeEvent[] = [];
  if (d.time_zone.mismatch && d.time_zone.suggested_offset_h !== null)
    out.push({
      event_key: `station_time_zone:ta_live:${station.imei}:${day}:${d.time_zone.suggested_offset_h}`,
      kind: 'station_time_zone',
      title: `Station time zone: ${name}`.slice(0, 120),
      body:
        d.time_zone.against === 'project'
          ? `${station.name}'s clock looks set to ${stationClockLabel(d.time_zone.suggested_offset_h)} (sensor day ${day}), but project ${d.time_zone.project_number ?? ''} is on ${stationClockLabel(d.time_zone.project_offset_h)}. Check the unit's clock zone.`
          : `${station.name}'s clock now looks set to ${stationClockLabel(d.time_zone.suggested_offset_h)} (sensor day ${day}); the day before it was ${stationClockLabel(d.time_zone.previous_offset_h)}. Check the unit's clock zone.`,
      occurred_at: iso(input.nowMs),
      project_id: null,
      asset_id: null,
      follows_key: null,
      detail: {
        ...detail,
        offset_h: d.time_zone.suggested_offset_h,
        previous_offset_h: d.time_zone.previous_offset_h ?? null,
        project_offset_h: d.time_zone.project_offset_h ?? null,
      },
    });
  if (d.drift.alert)
    out.push({
      event_key: `station_clock:ta_live:${station.imei}:${day}`,
      kind: 'station_clock',
      title: `Station clock: ${name}`.slice(0, 120),
      body: `${station.name}'s clock is ${sec(Math.abs(d.drift.mean_s))} ${d.drift.mean_s > 0 ? 'behind' : 'ahead'} (sensor day ${day}, after its usual ${sec(d.usual_delay_s ?? 0)} network delay). Check its GPS lock or set the time.`,
      occurred_at: iso(input.nowMs),
      project_id: null,
      asset_id: null,
      follows_key: null,
      detail: { ...detail, drift_s: d.drift.mean_s, usual_delay_s: d.usual_delay_s ?? null },
    });
  return out;
}

/**
 * A station's notices for the projects it is the base station of (parity C3): one event per project, keyed and
 * followed per project, so each goes to that project's crew (decision 53: basemag alerts to the field crew of the
 * project). A station on no project keeps its one notice for the whole organisation.
 */
export function projectStationNotices(
  events: readonly StationNoticeEvent[],
  projectIds: readonly string[] | null | undefined,
): StationNoticeEvent[] {
  const ids = [...new Set(projectIds ?? [])].sort();
  if (ids.length === 0) return [...events];
  return events.flatMap((e) =>
    ids.map((p) => ({
      ...e,
      event_key: `${e.event_key}:${p}`.slice(0, 300),
      follows_key: e.follows_key === null ? null : `${e.follows_key}:${p}`.slice(0, 300),
      project_id: p,
    })),
  );
}

// ------------------------------------------------------------------------------------------------ reading it (Field, Cloud)

/** The organisation's TA Live connection as `station_health(p_tenant)` gives it. */
export interface StationConnectorRow {
  readonly state: ConnectorStateId;
  readonly message: string;
  readonly failures: number;
  readonly paused_until: string | null;
  readonly last_poll_at: string | null;
  readonly last_ok_at: string | null;
  readonly next_poll_at: string | null;
  readonly reset_at: string | null;
}

export interface StationHealthRowHealth {
  readonly status: StationHealthStatus;
  readonly last_reading_at: string | null;
  readonly last_sensor_time: string | null;
  readonly last_value_nt: number | null;
  readonly last_good: boolean | null;
  readonly detected_offset_h: number | null;
  readonly message: string;
  readonly diagnostics: StationDiagnostics | null;
  readonly diagnostics_day: string | null;
  readonly offline_since: string | null;
  readonly checked_at: string;
}

export interface StationHealthRow {
  readonly id: string;
  readonly name: string;
  readonly station_number: string;
  readonly imei: string;
  readonly display_order: number;
  readonly clock_offset_h: number | null;
  readonly offline_alerts: boolean;
  readonly health: StationHealthRowHealth | null;
}

/** `station_health(p_tenant)`: the connection, the stations in display order, and when it was read. */
export interface StationHealthAnswer {
  readonly connector: StationConnectorRow | null;
  readonly stations: readonly StationHealthRow[];
  readonly online_minutes: number;
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v)
    ? v
    : typeof v === 'string' && v !== '' && Number.isFinite(Number(v))
      ? Number(v)
      : null;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const obj = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const STATUSES: readonly StationHealthStatus[] = ['online', 'offline', 'no_recent'];
const STATES: readonly ConnectorStateId[] = [
  'waiting',
  'ok',
  'failing',
  'paused',
  'refused',
  'not_set_up',
  'rate_limited',
];

/** Read the database's answer (or a kept copy) defensively: anything unreadable is left out. */
export function readStationHealth(raw: unknown): StationHealthAnswer {
  const a = obj(raw) ?? {};
  const c = obj(a.connector);
  const connector: StationConnectorRow | null = c
    ? {
        state: STATES.includes(c.state as ConnectorStateId) ? (c.state as ConnectorStateId) : 'waiting',
        message: str(c.message) ?? '',
        failures: num(c.failures) ?? 0,
        paused_until: str(c.paused_until),
        last_poll_at: str(c.last_poll_at),
        last_ok_at: str(c.last_ok_at),
        next_poll_at: str(c.next_poll_at),
        reset_at: str(c.reset_at),
      }
    : null;
  const stations: StationHealthRow[] = [];
  for (const s of Array.isArray(a.stations) ? a.stations : []) {
    const o = obj(s);
    if (!o || typeof o.id !== 'string' || typeof o.name !== 'string') continue;
    const h = obj(o.health);
    stations.push({
      id: o.id,
      name: o.name,
      station_number: str(o.station_number) ?? '',
      imei: str(o.imei) ?? '',
      display_order: num(o.display_order) ?? 0,
      clock_offset_h: num(o.clock_offset_h),
      offline_alerts: o.offline_alerts === true,
      health:
        h && STATUSES.includes(h.status as StationHealthStatus) && typeof h.checked_at === 'string'
          ? {
              status: h.status as StationHealthStatus,
              last_reading_at: str(h.last_reading_at),
              last_sensor_time: str(h.last_sensor_time),
              last_value_nt: num(h.last_value_nt),
              last_good: typeof h.last_good === 'boolean' ? h.last_good : null,
              detected_offset_h: num(h.detected_offset_h),
              message: str(h.message) ?? '',
              diagnostics: (obj(h.diagnostics) as StationDiagnostics | null) ?? null,
              diagnostics_day: str(h.diagnostics_day),
              offline_since: str(h.offline_since),
              checked_at: h.checked_at,
            }
          : null,
    });
  }
  stations.sort((x, y) => x.display_order - y.display_order || x.name.localeCompare(y.name));
  return { connector, stations, online_minutes: num(a.online_minutes) ?? STATION_ONLINE_MINUTES };
}

/** A unit's dot: green online, red offline (or no recent readings), amber not checked lately, grey not checked yet. */
export type StationDot = 'online' | 'offline' | 'stale' | 'unknown';

export function stationDot(row: StationHealthRow, nowMs: number): StationDot {
  const h = row.health;
  if (!h) return 'unknown';
  const checked = Date.parse(h.checked_at);
  if (!Number.isFinite(checked) || nowMs - checked > STATION_STALE_MS) return 'stale';
  return h.status === 'online' ? 'online' : 'offline';
}

/** The clock label beside a unit: the zone found, else the station's own. */
export function stationRowClock(row: StationHealthRow): string {
  const found = row.health?.detected_offset_h;
  if (found !== null && found !== undefined) return clockZoneLabel(found, 'detected');
  return row.clock_offset_h === null ? 'UTC?' : clockZoneLabel(row.clock_offset_h, 'registered');
}

/** "18 Oct 16:59" in a display zone. */
function at(ms: number, offsetH: number): string {
  const d = new Date(ms + Math.round(offsetH * MS_H));
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    d.getUTCMonth()
  ];
  return `${d.getUTCDate()} ${mon} ${iso(d.getTime()).slice(11, 16)}`;
}

/** One unit in words, for its tip and the health table: state, newest reading and when Teslyn Cloud checked. */
export function stationRowText(row: StationHealthRow, nowMs: number, displayOffsetH: number): string {
  const h = row.health;
  const name = unitName(row);
  if (!h) return `${name}: not checked by Teslyn Cloud yet.`;
  const dot = stationDot(row, nowMs);
  const zone = stationClockLabel(displayOffsetH);
  const checked = Date.parse(h.checked_at);
  const last = h.last_reading_at ? Date.parse(h.last_reading_at) : NaN;
  const state =
    dot === 'stale'
      ? `not checked since ${at(checked, displayOffsetH)} ${zone}`
      : h.status === 'online'
        ? 'online'
        : h.status === 'offline'
          ? 'offline'
          : 'no recent readings (none in the last days)';
  const reading = Number.isFinite(last)
    ? `newest reading ${at(last, displayOffsetH)} ${zone}${h.last_value_nt === null ? '' : `, ${h.last_value_nt.toFixed(1)} nT`}`
    : '';
  return (
    [`${name}: ${state}`, reading, `checked ${at(checked, displayOffsetH)} ${zone}`]
      .filter(Boolean)
      .join('; ') + '.'
  );
}

/** The connection in words (Teslyn Field's bar and the Station health page). */
export function connectorText(c: StationConnectorRow | null, nowMs: number, displayOffsetH: number): string {
  const zone = stationClockLabel(displayOffsetH);
  if (!c) return 'Teslyn Cloud has not checked the TA Live stations yet.';
  const when = (s: string | null): string => (s ? `${at(Date.parse(s), displayOffsetH)} ${zone}` : '');
  switch (c.state) {
    case 'ok':
      return `TA Live connection OK, checked ${when(c.last_poll_at)}.`;
    case 'waiting':
      return c.reset_at
        ? `Checks resumed ${when(c.reset_at)}; the next check runs within 5 minutes.`
        : 'Waiting for the first check.';
    case 'failing':
      return `TA Live did not answer the last ${plural(c.failures, 'check', 'checks')}; next try ${when(c.next_poll_at)}.`;
    case 'paused':
      return c.paused_until && Date.parse(c.paused_until) > nowMs
        ? `Checks paused after ${c.failures} unanswered checks, until ${when(c.paused_until)}. A manager can reset the connection.`
        : 'Checks resume at the next run.';
    case 'refused':
      return 'TA Live refused the stored key. A tenant admin replaces it in Teslyn Cloud, Integrations.';
    case 'not_set_up':
      return 'TA Live is not set up: a tenant admin adds the TA Live key in Teslyn Cloud, Integrations.';
    case 'rate_limited':
      return `Too many TA Live requests; next try ${when(c.next_poll_at)}.`;
  }
}

/** Who may reset the connection (AC-1.3: tenant admins and managers). */
export const STATION_RESET_ROLES = ['tenant_admin', 'manager'] as const;
/** Who may turn a station's offline alerts on or off (the roles that acknowledge alerts). */
export const STATION_ALERT_ROLES = ['tenant_admin', 'manager', 'field_crew', 'data_processor'] as const;

export function canResetStationConnector(roles: readonly string[]): boolean {
  return roles.some((r) => (STATION_RESET_ROLES as readonly string[]).includes(r));
}

export function canSetStationAlerts(roles: readonly string[]): boolean {
  return roles.some((r) => (STATION_ALERT_ROLES as readonly string[]).includes(r));
}
